@if (passwordResetEmailSent) {
  <section class="mfa-onboarding-panel mfa-onboarding-panel-standalone" role="status" aria-live="polite">
    <div class="mfa-email-callout">
      <fa-icon [icon]="faEnvelope" aria-hidden="true"></fa-icon>
      <span>Check your email for password reset instructions</span>
    </div>

    <div class="mfa-panel-header">
      <span class="mfa-panel-icon" aria-hidden="true">
        <fa-icon [icon]="faShieldHalved"></fa-icon>
      </span>
      <div>
        <h2 class="mfa-panel-title">Finish setting up your CRD sign-in</h2>
        <p class="mfa-panel-summary">
          Because this is your first sign-in since MFA became required, you will need to reset your password.
          We sent a password setup email to the address you entered. Complete that step first,
          then return to CRD and sign in with your new password. You will then be asked to set up
          multi-factor authentication (MFA) with an authenticator app.
        </p>
      </div>
    </div>

    <div class="mfa-panel-body">
      <div class="mfa-next-step">
        <span class="mfa-step-number">1</span>
        <span>Open the password setup email and follow the link to create your password.</span>
      </div>
      <div class="mfa-next-step">
        <span class="mfa-step-number">2</span>
        <span>Return to CRD, click Log In, and sign in with your email address and new password.</span>
      </div>
      <div class="mfa-next-step">
        <span class="mfa-step-number">3</span>
        <span>
          When prompted for setting up multi-factor authentication (MFA), scan the QR code with an authenticator app
          and enter the one-time code.
        </span>
      </div>
    </div>

    <a
      class="btn mfa-instructions-link"
      [href]="firstTimeSignInInstructionsUrl"
      target="_blank"
      rel="noopener noreferrer">
      <fa-icon class="mfa-instructions-icon" [icon]="faFileAlt" aria-hidden="true"></fa-icon>
      <span>For more detailed instructions, see the <strong>First-Time Sign-In Instructions</strong>.</span>
      <fa-icon class="mfa-instructions-external-icon" [icon]="faArrowUpRightFromSquare" aria-hidden="true"></fa-icon>
    </a>

    <div class="mfa-apps">
      <div class="mfa-apps-label">
        <fa-icon [icon]="faMobileScreenButton" aria-hidden="true"></fa-icon>
        Authenticator apps you can use with Auth0 MFA
      </div>
      <ul class="mfa-app-list">
        @for (app of authenticatorApps; track app.name) {
          <li>
            <a
              class="mfa-app-link"
              [href]="getAuthenticatorAppStoreUrl(app)"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="'Open ' + app.name + ' in the app store'">
              <span>{{ app.name }}</span>
              <fa-icon [icon]="faArrowUpRightFromSquare" aria-hidden="true"></fa-icon>
            </a>
          </li>
        }
      </ul>
    </div>

    <p class="mfa-email-reminder">
      <fa-icon [icon]="faEnvelope" aria-hidden="true"></fa-icon>
      No email? Check your spam or junk folder, then contact CRD support if it still has not arrived.
    </p>

    <div class="mfa-panel-actions">
      <button type="button" class="btn btn-primary mfa-login-button" (click)="showLoginForm()">
        <fa-icon [icon]="faSignInAlt" aria-hidden="true"></fa-icon>
        Log In
      </button>
    </div>
  </section>
} @else {
  <div class="login-layout">
    <div class="card page-card login-card">
      <div class="card-body">
        <div class="row">
          <div class="col-md-12">
            <div class="login-heading">
              <p class="login-kicker">Existing account</p>
              <h2>Login</h2>
            </div>

            <form id="loginForm" [formGroup]="loginForm" (ngSubmit)="submitForm()" novalidate>
              <div class="form-group">
                <label for="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  formControlName="email"
                  class="form-control"
                  autocomplete="email"
                  [class.is-invalid]="accountNotFound || (emailControl.invalid && (emailControl.dirty || emailControl.touched))"
                  required />
                @if (emailControl.invalid && (emailControl.dirty || emailControl.touched)) {
                  <small class="invalid-feedback">Enter a valid email address.</small>
                } @else if (accountNotFound) {
                  <small class="invalid-feedback">Account not found.</small>
                }
              </div>

              <aside class="login-mfa-reminder" aria-label="Multi-factor authentication reminder">
                <span class="login-mfa-reminder-icon" aria-hidden="true">
                  <fa-icon [icon]="faMobileScreen"></fa-icon>
                </span>
                <p>
                  <strong>Have your phone ready</strong>
                  <span>You’ll need it for multi-factor authentication.</span>
                </p>
                <a
                  class="login-mfa-info-link"
                  [href]="firstTimeSignInInstructionsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open first-time sign-in instructions in a new tab"
                  title="First-Time Sign-In Instructions">
                  <fa-icon [icon]="faFileAlt" aria-hidden="true"></fa-icon>
                </a>
              </aside>

              <aside class="login-mfa-help" aria-labelledby="login-mfa-help-title">
                <h3 id="login-mfa-help-title">Google Authenticator not working?</h3>
                <p>
                  New phone or no working sign-in code? Ask your agency admin to reset your MFA.
                  Once they confirm the reset, follow this guide.
                </p>
                <a [href]="mfaReEnrollmentGuideUrl" target="_blank" rel="noopener noreferrer">
                  Set up Google Authenticator again (PDF)
                  <fa-icon [icon]="faArrowUpRightFromSquare" aria-hidden="true"></fa-icon>
                  <span class="visually-hidden">(opens in a new tab)</span>
                </a>
              </aside>

              <button
                type="submit"
                class="btn btn-primary mt-3 login-submit-button"
                [disabled]="loginForm.invalid || isSubmitting">
                <span>Continue</span>
                @if(isSubmitting){
                  <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                } @else {
                  <fa-icon [icon]="faArrowRight" aria-hidden="true"></fa-icon>
                }
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>

    <section class="registration-panel" aria-labelledby="registration-title">
      <div>
        <p class="registration-kicker">New provider organization</p>
        <h2 id="registration-title">Register a new agency</h2>
        <p>Start an agency registration when your organization is not already part of CRD.</p>
      </div>
      <a routerLink="/new-account" class="btn btn-outline-success registration-button">
        Register Your Agency
      </a>
      <p class="registration-access-note">
        <fa-icon [icon]="faInfoCircle" aria-hidden="true"></fa-icon>
        <span>Joining an existing agency? Contact your agency administrator to have a login created.</span>
      </p>
    </section>
  </div>
}
